import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import MyResumeCard from "@/components/MyResumeCard";
import { resolveAiResponse } from "@/hooks/useAiJob";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    EnvelopeSimple,
    Phone,
    MapPin,
    Briefcase,
    CurrencyInr,
    Sparkle,
    FileText,
    Copy as CopyIcon,
    ArrowRight,
    ChatCenteredDots,
    CalendarBlank,
    Clock as ActivityIcon,
    LinkedinLogo,
    ClipboardText,
    X,
    WhatsappLogo,
    PaperPlaneTilt,
    Broadcast,
    CheckCircle,
    XCircle,
    ClockCounterClockwise,
    Robot,
    ArrowClockwise,
} from "@phosphor-icons/react";
import OfferHireOutcomeModal from "./OfferHireOutcomeModal";
import ResumeActions from "./ResumeActions";
import StageTimeline from "./StageTimeline";
import AddedByChip from "./AddedByChip";
import VerifiedTierChip from "./VerifiedTierChip";
import ReassignCandidateModal from "./ReassignCandidateModal";
import { useAuth } from "@/context/AuthContext";

const STAGES = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];
const STAGE_ORDER = ["Applied", "Screening", "Interview", "Offer", "Hired"];
const nextStageOf = (cur) => {
    const i = STAGE_ORDER.indexOf(cur);
    return i < 0 || i >= STAGE_ORDER.length - 1 ? null : STAGE_ORDER[i + 1];
};

const stageCls = (s) => ({
    Applied: "bg-[#F5F5F5] text-[#525252]",
    Screening: "bg-[#EEF2FF] text-[#3730A3]",
    Interview: "bg-[#EFF6FF] text-[#F59E0B]",
    Offer: "bg-[#FAF5FF] text-[#7E22CE]",
    Hired: "bg-[#ECFDF5] text-[#059669]",
    Rejected: "bg-[#FEF2F2] text-[#DC2626]",
}[s] || "");

function ActivityDot({ kind }) {
    const meta = {
        created: { color: "bg-cobalt", label: "+" },
        stage: { color: "bg-[#F59E0B]", label: "→" },
        note: { color: "bg-[#7E22CE]", label: "•" },
        interview: { color: "bg-[#059669]", label: "◆" },
    }[kind] || { color: "bg-[#A3A3A3]", label: "•" };
    return (
        <span className={`inline-flex items-center justify-center w-5 h-5 text-white text-[10px] font-black ${meta.color}`}>
            {meta.label}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso.slice(0, 10); }
}

function fmtDateTime(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function ctcFormat(amt, cur) {
    if (amt == null) return null;
    const symbols = { INR: "₹", USD: "$", GBP: "£", EUR: "€", AED: "د.إ", SGD: "S$" };
    const s = symbols[cur] || `${cur} `;
    if (cur === "INR" && amt >= 100000) {
        return `${s}${(amt / 100000).toFixed(2)} L`;
    }
    return `${s}${Number(amt).toLocaleString()}`;
}

function fmtSalary(min, max) {
    if (min == null && max == null) return null;
    const f = (v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v.toLocaleString());
    if (min != null && max != null) return `₹${f(min)} – ₹${f(max)}`;
    return `₹${f(min ?? max)}${min == null ? " (max)" : "+"}`;
}

export default function PersonDetailPanel({ open, cid, onClose, onOpenPosting, jobs, onCandidateChange = null }) {
    const { user: currentUser } = useAuth();
    const canReassign = ["admin", "hiring_manager", "superadmin"].includes(currentUser?.role);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("overview");
    const [movingCid, setMovingCid] = useState(null);
    const [showCopyTo, setShowCopyTo] = useState(false);
    const [copyTargetJob, setCopyTargetJob] = useState("");
    const [copyBusy, setCopyBusy] = useState(false);
    // Feb 9, 2026 — recruiter data isolation. Admin/HM can reassign
    // ownership from this panel; other roles never see the button.
    const [showReassign, setShowReassign] = useState(false);
    // Add-note-across-postings
    const [personNote, setPersonNote] = useState("");
    const [personNoteBusy, setPersonNoteBusy] = useState(false);
    // Reach-out quick draft
    const [showReachOut, setShowReachOut] = useState(false);
    // Feb 2026 — sub-status outcome modal (Offer accepted/rejected, Hired/no-show).
    const [outcomeState, setOutcomeState] = useState(null); // { kind, cid, existing }
    // AI summary card
    const [aiSummary, setAiSummary] = useState(null); // { text, generated_at, model }
    const [aiLoading, setAiLoading] = useState(false);
    const [aiErr, setAiErr] = useState("");
    const [reachChannel, setReachChannel] = useState("email");   // email | whatsapp
    const [reachTemplate, setReachTemplate] = useState("intro");
    const [reachSubject, setReachSubject] = useState("");
    const [reachBody, setReachBody] = useState("");
    const [sendingLoginLink, setSendingLoginLink] = useState(false);

    // Recruiter: email the candidate a passwordless magic sign-in link.
    // Reusable 30-day token — the candidate can bookmark it. See
    // routes/candidates.py `send_candidate_login_link`.
    const sendLoginLink = async () => {
        if (!cid) return;
        setSendingLoginLink(true);
        try {
            const { data } = await api.post(`/candidates/${cid}/send-login-link`);
            toast.success(data?.message || "Sign-in link sent.");
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Could not send the login link.");
        } finally {
            setSendingLoginLink(false);
        }
    };

    const reload = async () => {
        if (!cid) return;
        setLoading(true);
        try {
            const r = await api.get(`/candidates/${cid}/person`);
            setData(r.data);
        } catch (e) {
            logError("person fetch failed", e);
            // Feb 9, 2026 — recruiter isolation: 403 means the candidate
            // belongs to another recruiter. Show the transparent "belongs
            // to Ravi" message from the backend and close the panel so
            // the user doesn't stare at an empty drawer.
            const detail = e?.response?.data?.detail;
            const message =
                typeof detail === "object" && detail?.message
                    ? detail.message
                    : (typeof detail === "string" ? detail : "Couldn't load person details.");
            toast.error(message);
            if (e?.response?.status === 403) {
                onClose?.();
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && cid) {
            setTab("overview");
            setAiSummary(null);
            setAiErr("");
            reload();
        } else {
            setData(null);
        }
    }, [open, cid]);

    // Fetch cached AI summary once the person 360 loads.
    useEffect(() => {
        if (!open || !cid || !data) return;
        let alive = true;
        api.get(`/candidates/${cid}/person/ai-summary`)
            .then((r) => { if (alive) setAiSummary(r.data || null); })
            .catch(() => { /* ignore — first-time users won't have one */ });
        return () => { alive = false; };
    }, [open, cid, data]);

    const generateAiSummary = async () => {
        if (!cid) return;
        setAiLoading(true);
        setAiErr("");
        try {
            const r = await api.post(`/candidates/${cid}/person/ai-summary`);
            // V1.9.0 — resolve async {job_id} → poll → final summary doc.
            const data = await resolveAiResponse(r.data);
            setAiSummary(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Couldn't generate summary.";
            setAiErr(msg);
            toast.error(msg);
        } finally {
            setAiLoading(false);
        }
    };

    const person = data?.person || {};
    const postings = data?.postings || [];
    const activity = data?.activity || [];
    const documents = data?.documents || [];

    const moveStage = async (posting) => {
        const next = nextStageOf(posting.stage);
        if (!next) return;
        // Feb 2026 — Mandatory-registration gate.
        if (
            !posting.candidate_user_id &&
            posting.drip_state?.status === "active" &&
            !["Applied", "Rejected"].includes(next)
        ) {
            toast.info(`Candidate hasn't registered yet — send a reminder before moving to ${next}.`, { duration: 5000 });
            return;
        }
        setMovingCid(posting.candidate_id);
        try {
            await api.patch(`/candidates/${posting.candidate_id}/stage`, { stage: next });
            toast.success(`Moved to ${next} on ${posting.job_title || "requirement"}.`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update stage.");
        } finally {
            setMovingCid(null);
        }
    };

    const doCopy = async () => {
        if (!copyTargetJob || !postings.length) return;
        setCopyBusy(true);
        try {
            const r = await api.post("/candidates/bulk/copy", {
                candidate_ids: [postings[0].candidate_id],
                target_job_id: copyTargetJob,
            });
            const d = r.data || {};
            if (d.created > 0) {
                toast.success(`Copied to “${d.target_job_title}”.`);
                setShowCopyTo(false);
                setCopyTargetJob("");
                reload();
            } else {
                toast.info(`Already on that requirement — nothing to copy.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Copy failed.");
        } finally {
            setCopyBusy(false);
        }
    };

    // ---- Add note across all postings ----
    const submitPersonNote = async () => {
        const text = personNote.trim();
        if (!text) return;
        setPersonNoteBusy(true);
        try {
            const r = await api.post(`/candidates/${cid}/person/notes`, { text });
            const d = r.data || {};
            toast.success(`Note added to ${d.updated || 0} posting${d.updated === 1 ? "" : "s"}.`);
            setPersonNote("");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save note.");
        } finally {
            setPersonNoteBusy(false);
        }
    };

    // ---- Reach out quick draft ----
    const REACH_TEMPLATES = {
        intro: {
            label: "Job intro",
            subject: "Opportunity — {job_title} at {client_name}",
            body: `Hi {first_name},

Hope you're well! I'm reaching out about a {job_title} role at {client_name}. Based on your background at {current_title}, I think this could be a strong fit.

Would you be open to a quick 15-min chat this week?

— {recruiter_name}`,
        },
        followup: {
            label: "Screening follow-up",
            subject: "Following up — {job_title}",
            body: `Hi {first_name},

Just circling back on your application for {job_title} at {client_name}. Do you have 15 minutes this week for a quick screening call?

Best,
{recruiter_name}`,
        },
        interview: {
            label: "Interview slot ask",
            subject: "Interview slots — {job_title}",
            body: `Hi {first_name},

Great news — {client_name} would like to move you to the interview round for {job_title}. Please share 2-3 slots that work for you over the next few days.

— {recruiter_name}`,
        },
        offer_congrats: {
            label: "Congratulations on hire",
            subject: "Congrats on joining {client_name}!",
            body: `Hi {first_name},

Congratulations on the offer! It was a pleasure supporting your journey to {client_name} for {job_title}. Wishing you all the best.

— {recruiter_name}`,
        },
    };

    const currentUserName = useMemo(() => {
        try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            return u?.name || "the recruiter";
        } catch { return "the recruiter"; }
    }, []);

    const interpolate = (tpl) => {
        const primary = postings[0] || {};
        const firstName = (person.name || "").split(/\s+/)[0] || "there";
        return (tpl || "")
            .replaceAll("{name}", person.name || "")
            .replaceAll("{first_name}", firstName)
            .replaceAll("{job_title}", primary.job_title || "the role")
            .replaceAll("{client_name}", primary.client_name || "our client")
            .replaceAll("{current_title}", person.current_title || "your current role")
            .replaceAll("{recruiter_name}", currentUserName);
    };

    const openReachOut = (channel) => {
        setReachChannel(channel);
        const t = REACH_TEMPLATES[reachTemplate] || REACH_TEMPLATES.intro;
        setReachSubject(interpolate(t.subject));
        setReachBody(interpolate(t.body));
        setShowReachOut(true);
    };

    const applyReachTemplate = (key) => {
        setReachTemplate(key);
        const t = REACH_TEMPLATES[key] || REACH_TEMPLATES.intro;
        setReachSubject(interpolate(t.subject));
        setReachBody(interpolate(t.body));
    };

    const sendReachOut = () => {
        if (reachChannel === "whatsapp") {
            if (!person.phone) { toast.error("No phone number on file."); return; }
            const digits = String(person.phone).replace(/[^0-9]/g, "");
            const msg = `${reachSubject ? reachSubject + "\n\n" : ""}${reachBody}`;
            const url = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
            window.open(url, "_blank", "noopener,noreferrer");
            toast.success("Opened WhatsApp draft.");
        } else {
            if (!person.email) { toast.error("No email on file."); return; }
            const url = `mailto:${person.email}?subject=${encodeURIComponent(reachSubject)}&body=${encodeURIComponent(reachBody)}`;
            window.location.href = url;
            toast.success("Opened email compose.");
        }
        setShowReachOut(false);
    };


    const eligibleJobs = useMemo(() => {
        const takenJobIds = new Set(postings.map((p) => p.job_id));
        return (jobs || []).filter((j) => !takenJobIds.has(j.id));
    }, [jobs, postings]);

    const initials = useMemo(() => {
        const n = person.name || "";
        return n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
    }, [person.name]);

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <SheetContent
                data-testid="person-detail-panel"
                side="right"
                className="w-full sm:max-w-3xl p-0 flex flex-col overflow-hidden"
            >
                {/* --- Header --- */}
                <SheetHeader className="p-4 sm:p-6 border-b border-[#E5E5E5] bg-white">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div
                                data-testid="person-avatar"
                                className="w-12 h-12 sm:w-14 sm:h-14 bg-cobalt text-white font-black text-lg sm:text-xl flex items-center justify-center shrink-0 overflow-hidden"
                            >
                                {person.avatar_url ? (
                                    <img
                                        src={person.avatar_url}
                                        alt={person.name || "candidate"}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    initials
                                )}
                            </div>
                            <div className="min-w-0">
                                <SheetTitle className="text-xl sm:text-2xl font-black tracking-tight text-ink truncate">
                                    {person.name || "…"}
                                </SheetTitle>
                                <SheetDescription className="sr-only">
                                    360° profile for {person.name || "candidate"} —
                                    postings, notes, activity, documents and outbound
                                    reach-out drafts.
                                </SheetDescription>
                                <p className="text-xs text-[#525252] truncate">
                                    {person.current_title || "—"}
                                </p>
                                <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                                    <span
                                        data-testid="person-posting-count"
                                        className="text-[10px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5"
                                    >
                                        {data?.posting_count || 0} req{(data?.posting_count || 0) === 1 ? "" : "s"}
                                    </span>
                                    {data?.furthest_stage && (
                                        <span
                                            data-testid="person-furthest-stage"
                                            className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 ${stageCls(data.furthest_stage)}`}
                                        >
                                            {data.furthest_stage}
                                        </span>
                                    )}
                                    {data?.has_been_rejected && data?.furthest_stage !== "Rejected" && (
                                        <span
                                            data-testid="person-rejected-before"
                                            title="This person has been rejected on at least one previous requirement."
                                            className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-[#FEE2E2] text-[#B91C1C]"
                                        >
                                            Rejected before
                                        </span>
                                    )}
                                    <AddedByChip
                                        name={person.added_by_name || data?.added_by_name}
                                        isTeamPool={!!person.is_team_pool || !!data?.is_team_pool}
                                        isMine={
                                            !!(person.owner_id || data?.owner_id) &&
                                            (person.owner_id || data?.owner_id) === currentUser?.id
                                        }
                                        size="sm"
                                    />
                                    <VerifiedTierChip tier={person?.verification_tier} size="sm" />
                                </div>
                            </div>
                        </div>
                        {/* Note: `SheetContent` from shadcn/ui already renders a
                            close X in the top-right — no need for a duplicate here. */}
                    </div>
                    {/* Quick actions row */}
                    <div className="mt-3 flex flex-wrap gap-2">
                        <a
                            href={person.email ? `mailto:${person.email}` : undefined}
                            data-testid="person-email-btn"
                            className="text-[10px] font-black uppercase tracking-wider px-2 py-1.5 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink inline-flex items-center gap-1"
                        >
                            <EnvelopeSimple size={12} weight="bold" /> Email
                        </a>
                        {person.phone && (
                            <a
                                href={`tel:${person.phone}`}
                                data-testid="person-call-btn"
                                className="text-[10px] font-black uppercase tracking-wider px-2 py-1.5 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink inline-flex items-center gap-1"
                            >
                                <Phone size={12} weight="bold" /> Call
                            </a>
                        )}
                        {person.linkedin_url && (
                            <a
                                href={person.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] font-black uppercase tracking-wider px-2 py-1.5 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink inline-flex items-center gap-1"
                            >
                                <LinkedinLogo size={12} weight="bold" /> LinkedIn
                            </a>
                        )}
                        <button
                            data-testid="person-copy-to-btn"
                            onClick={() => setShowCopyTo((v) => !v)}
                            className="text-[10px] font-black uppercase tracking-wider px-2 py-1.5 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink inline-flex items-center gap-1"
                        >
                            <CopyIcon size={12} weight="bold" /> Copy to job
                        </button>
                        <button
                            data-testid="person-reach-out-btn"
                            onClick={() => openReachOut("email")}
                            className="text-[10px] font-black uppercase tracking-wider px-2 py-1.5 bg-cobalt text-white inline-flex items-center gap-1 hover:opacity-90"
                        >
                            <PaperPlaneTilt size={12} weight="bold" /> Reach out
                        </button>
                        {person.email && (
                            <button
                                data-testid="person-send-login-link-btn"
                                onClick={sendLoginLink}
                                disabled={sendingLoginLink}
                                title={`Email a passwordless sign-in link to ${person.email}. Valid for 30 days.`}
                                className="text-[10px] font-black uppercase tracking-wider px-2 py-1.5 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1"
                            >
                                <EnvelopeSimple size={12} weight="bold" />
                                {sendingLoginLink ? "Sending…" : "Send login link"}
                            </button>
                        )}
                        {canReassign && (
                            <button
                                data-testid="person-reassign-btn"
                                onClick={() => setShowReassign(true)}
                                title="Reassign ownership of this candidate to a different recruiter, or move to the shared team pool."
                                className="text-[10px] font-black uppercase tracking-wider px-2 py-1.5 border border-cobalt text-cobalt hover:bg-cobalt hover:text-white inline-flex items-center gap-1 transition-colors"
                            >
                                Reassign
                            </button>
                        )}
                    </div>
                    {showCopyTo && (
                        <div data-testid="person-copy-panel" className="mt-3 flex flex-wrap gap-2 items-center bg-[#F9F9F9] p-3">
                            <select
                                data-testid="person-copy-target"
                                value={copyTargetJob}
                                onChange={(e) => setCopyTargetJob(e.target.value)}
                                className="h-9 px-2 pr-7 text-xs bg-white border border-[#E5E5E5] flex-1 min-w-[180px] focus:outline-none focus:border-cobalt"
                            >
                                <option value="">Choose a requirement…</option>
                                {eligibleJobs.map((j) => (
                                    <option key={j.id} value={j.id}>{j.title}</option>
                                ))}
                            </select>
                            <button
                                data-testid="person-copy-submit"
                                onClick={doCopy}
                                disabled={!copyTargetJob || copyBusy}
                                className="px-3 py-2 text-[10px] font-black uppercase tracking-wider bg-ink text-white disabled:opacity-50"
                            >
                                {copyBusy ? "Copying…" : "Copy"}
                            </button>
                        </div>
                    )}
                </SheetHeader>

                {/* --- Tabs --- */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 text-center text-sm text-[#A3A3A3]">Loading 360° view…</div>
                    ) : !data ? (
                        <div className="p-8 text-center text-sm text-[#A3A3A3]">No details available.</div>
                    ) : (
                        <Tabs value={tab} onValueChange={setTab} className="w-full">
                            {/* V2.0.74 — Profile-only banner when this
                                candidate belongs to another recruiter and
                                workspace full-visibility is ON. */}
                            {data._pipeline_redacted && (
                                <div
                                    data-testid="person-profile-only-banner"
                                    className="border-b border-amber-300 bg-amber-50 text-amber-900 px-4 py-2.5 text-xs leading-relaxed"
                                >
                                    <span className="font-black uppercase tracking-wider mr-1">Profile only</span>
                                    &middot;{" "}
                                    {data.added_by_name
                                        ? <>Owned by <strong>{data.added_by_name}</strong>. Postings, notes, tags and activity stay in their Kanban.</>
                                        : <>Postings, notes, tags and activity stay private to the owner&apos;s Kanban.</>
                                    }
                                </div>
                            )}
                            <TabsList className={`w-full grid ${data._pipeline_redacted ? "grid-cols-2" : "grid-cols-4"} h-11 rounded-none bg-white border-b border-[#E5E5E5] sticky top-0 z-10`}>
                                <TabsTrigger data-testid="person-tab-overview" value="overview" className="text-[10px] font-black uppercase tracking-wider">Overview</TabsTrigger>
                                {!data._pipeline_redacted && <TabsTrigger data-testid="person-tab-postings" value="postings" className="text-[10px] font-black uppercase tracking-wider">Postings ({postings.length})</TabsTrigger>}
                                <TabsTrigger data-testid="person-tab-docs" value="docs" className="text-[10px] font-black uppercase tracking-wider">Docs ({documents.length})</TabsTrigger>
                                {!data._pipeline_redacted && <TabsTrigger data-testid="person-tab-activity" value="activity" className="text-[10px] font-black uppercase tracking-wider">Activity</TabsTrigger>}
                            </TabsList>

                            {/* Overview */}
                            <TabsContent value="overview" className="p-4 sm:p-6 space-y-5">
                                {/* AI person summary */}
                                <section
                                    data-testid="person-ai-summary"
                                    className="border border-[#E5E5E5] bg-gradient-to-br from-splash/5 via-white to-cobalt/5 p-4"
                                >
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt inline-flex items-center gap-1.5">
                                                <Robot size={11} weight="bold" /> AI summary
                                            </p>
                                            {aiSummary?.generated_at && (
                                                <p className="text-[10px] text-[#A3A3A3] mt-0.5">
                                                    Generated {fmtDateTime(aiSummary.generated_at)}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            data-testid="person-ai-summary-generate"
                                            onClick={generateAiSummary}
                                            disabled={aiLoading}
                                            className="shrink-0 border border-[#E5E5E5] hover:border-cobalt hover:text-cobalt px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 bg-white"
                                        >
                                            <ArrowClockwise size={10} weight="bold" className={aiLoading ? "animate-spin" : ""} />
                                            {aiLoading ? "Generating…" : aiSummary ? "Regenerate" : "Generate"}
                                        </button>
                                    </div>
                                    {aiErr && !aiLoading && (
                                        <p className="text-xs text-[#DC2626] mb-1">{aiErr}</p>
                                    )}
                                    {aiLoading ? (
                                        <div className="space-y-1.5 pt-1" data-testid="person-ai-summary-loading">
                                            <div className="h-2 bg-[#E5E5E5] animate-pulse w-11/12" />
                                            <div className="h-2 bg-[#E5E5E5] animate-pulse w-10/12" />
                                            <div className="h-2 bg-[#E5E5E5] animate-pulse w-9/12" />
                                            <div className="h-2 bg-[#E5E5E5] animate-pulse w-7/12" />
                                        </div>
                                    ) : aiSummary?.text ? (
                                        <p
                                            data-testid="person-ai-summary-text"
                                            className="text-sm text-ink leading-relaxed whitespace-pre-line"
                                        >
                                            {aiSummary.text}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-[#525252] italic">
                                            Click <span className="font-bold text-ink">Generate</span> for a one-paragraph recap
                                            of {person.name?.split(" ")[0] || "this candidate"}&apos;s pipeline journey — powered by NaukriDaddy AI.
                                        </p>
                                    )}
                                </section>

                                <section>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Contact</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <EnvelopeSimple size={14} weight="bold" className="text-[#A3A3A3] shrink-0" />
                                            <span className="truncate">{person.email || "—"}</span>
                                        </div>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Phone size={14} weight="bold" className="text-[#A3A3A3] shrink-0" />
                                            <span className="truncate">{person.phone || "—"}</span>
                                        </div>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <MapPin size={14} weight="bold" className="text-[#A3A3A3] shrink-0" />
                                            <span className="truncate">{person.location || "—"}</span>
                                        </div>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Briefcase size={14} weight="bold" className="text-[#A3A3A3] shrink-0" />
                                            <span className="truncate">
                                                {person.years_experience != null
                                                    ? `${person.years_experience}y experience`
                                                    : (person.current_title || "—")}
                                            </span>
                                        </div>
                                        {fmtSalary(person.expected_salary_min, person.expected_salary_max) && (
                                            <div className="flex items-center gap-2 min-w-0 sm:col-span-2">
                                                <CurrencyInr size={14} weight="bold" className="text-[#A3A3A3] shrink-0" />
                                                <span className="truncate">{fmtSalary(person.expected_salary_min, person.expected_salary_max)}</span>
                                            </div>
                                        )}
                                    </div>
                                </section>

                                {(person.skills || []).length > 0 && (
                                    <section>
                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Skills</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(person.skills || []).map((s) => (
                                                <span key={s} className="text-[10px] font-black uppercase tracking-wider bg-[#F5F5F5] text-[#525252] px-1.5 py-1">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {(person.tags || []).length > 0 && (
                                    <section>
                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Tags (union across all postings)</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(person.tags || []).map((t) => (
                                                <span key={t} className="text-[10px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-1">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Add note across all postings */}
                                <section>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 flex items-center gap-1">
                                        <Broadcast size={11} weight="bold" /> Add note (shared across all {postings.length} posting{postings.length === 1 ? "" : "s"})
                                    </p>
                                    <div className="flex gap-2">
                                        <textarea
                                            data-testid="person-note-input"
                                            value={personNote}
                                            onChange={(e) => setPersonNote(e.target.value)}
                                            placeholder="e.g. Candidate expressed interest in remote roles — share fully-remote openings"
                                            rows={2}
                                            maxLength={2000}
                                            className="flex-1 px-3 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt resize-none"
                                        />
                                        <button
                                            type="button"
                                            data-testid="person-note-submit"
                                            onClick={submitPersonNote}
                                            disabled={personNoteBusy || !personNote.trim()}
                                            className="px-3 py-2 text-[10px] font-black uppercase tracking-wider bg-ink text-white disabled:opacity-50 shrink-0"
                                        >
                                            {personNoteBusy ? "Adding…" : "Add"}
                                        </button>
                                    </div>
                                </section>

                                {/* Snapshot of postings */}
                                <section>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Postings snapshot</p>
                                    <div className="flex flex-col gap-2">
                                        {postings.map((p) => {
                                            const next = nextStageOf(p.stage);
                                            return (
                                                <div key={p.candidate_id} data-testid={`overview-posting-${p.candidate_id}`} className="border border-[#E5E5E5] p-3 flex items-center justify-between gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setTab("postings")}
                                                        className="min-w-0 flex-1 text-left"
                                                    >
                                                        <p className="font-bold text-sm text-ink truncate">{p.job_title || "—"}</p>
                                                        <p className="text-[10px] text-[#A3A3A3] truncate">{p.client_name || ""} · added {fmtDate(p.created_at)}</p>
                                                    </button>
                                                    <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${stageCls(p.stage)}`}>
                                                        {p.stage}
                                                    </span>
                                                    {next && (
                                                        <button
                                                            type="button"
                                                            data-testid={`overview-move-${p.candidate_id}`}
                                                            onClick={() => moveStage(p)}
                                                            disabled={movingCid === p.candidate_id}
                                                            className="text-[9px] font-black uppercase tracking-wider px-2 py-1 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink disabled:opacity-50 inline-flex items-center gap-1 shrink-0"
                                                        >
                                                            <ArrowRight size={10} weight="bold" /> {next.slice(0, 3)}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            </TabsContent>

                            {/* Postings — full detail per JD */}
                            <TabsContent value="postings" className="p-4 sm:p-6 space-y-5">
                                {postings.map((p) => (
                                    <div key={p.candidate_id} data-testid={`posting-block-${p.candidate_id}`} className="border border-[#E5E5E5] p-4 space-y-3">
                                        <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-bold text-ink truncate">{p.job_title || "—"}</p>
                                                <p className="text-[10px] text-[#A3A3A3] truncate">{p.client_name || ""} · {p.job_location || ""}</p>
                                                <p className="text-[10px] text-[#A3A3A3] font-mono mt-1">{p.code || ""} · added {fmtDate(p.created_at)}</p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${stageCls(p.stage)}`}>{p.stage}</span>
                                                {p.match_score != null && (
                                                    <span className="inline-flex items-center gap-1 text-xs font-black text-ink">
                                                        <Sparkle size={11} weight="fill" className="text-cobalt" /> {p.match_score}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Registration drip status — mandatory registration gate.
                                            Shown only when candidate hasn't self-registered yet. */}
                                        {!p.candidate_user_id && p.drip_state && p.drip_state.status === "active" && (
                                            <div
                                                data-testid={`posting-${p.candidate_id}-drip-banner`}
                                                className="border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5"
                                            >
                                                <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-bold text-amber-900 mb-0.5">
                                                        Awaiting portal registration
                                                    </p>
                                                    <p className="text-[11px] text-amber-800 leading-relaxed">
                                                        Reached out{" "}
                                                        <span className="tabular-nums font-bold">
                                                            {p.drip_state.sent_count || 0}
                                                        </span>{" "}
                                                        of 5 times.{" "}
                                                        {p.drip_state.sent_count > 0 && p.drip_state.history?.length > 0 && (
                                                            <>Last: {fmtDate(p.drip_state.history[p.drip_state.history.length - 1]?.sent_at)}. </>
                                                        )}
                                                        Stage cannot advance past Applied until they register.
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    data-testid={`posting-${p.candidate_id}-nudge-now`}
                                                    onClick={async () => {
                                                        try {
                                                            const r = await api.post(`/candidates/${p.candidate_id}/nudge-registration`);
                                                            toast.success(`Reminder sent (${r.data.drip_state?.sent_count || 0}/5).`);
                                                            reload();
                                                        } catch (ex) {
                                                            toast.error(ex?.response?.data?.detail || "Nudge failed.");
                                                        }
                                                    }}
                                                    className="shrink-0 bg-amber-900 hover:bg-amber-950 text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1.5"
                                                >
                                                    Send reminder now
                                                </button>
                                            </div>
                                        )}
                                        {!p.candidate_user_id && p.drip_state && p.drip_state.final_action === "auto_rejected" && (
                                            <div
                                                data-testid={`posting-${p.candidate_id}-auto-rejected-banner`}
                                                className="border border-rose-300 bg-rose-50 px-3 py-2.5 text-xs text-rose-900"
                                            >
                                                <span className="font-bold">Auto-closed</span> — no response after 5 registration reminders.
                                            </div>
                                        )}

                                        {/* Stage move buttons — Feb 2026: disabled when candidate hasn't
                                            registered yet (except Applied + Rejected which are always allowed) */}
                                        <div className="flex flex-wrap gap-1.5">
                                            {STAGES.map((s) => {
                                                const isCurrent = s === p.stage;
                                                const gated =
                                                    !p.candidate_user_id &&
                                                    p.drip_state?.status === "active" &&
                                                    !["Applied", "Rejected"].includes(s);
                                                return (
                                                    <button
                                                        key={s}
                                                        type="button"
                                                        data-testid={`posting-${p.candidate_id}-stage-${s.toLowerCase()}`}
                                                        disabled={gated}
                                                        title={gated ? "Candidate hasn't registered on the portal yet. Send a reminder first." : undefined}
                                                        onClick={async () => {
                                                            if (isCurrent) return;
                                                            if (gated) {
                                                                toast.info("Candidate hasn't registered yet — send a reminder first.", { duration: 4000 });
                                                                return;
                                                            }
                                                            try {
                                                                await api.patch(`/candidates/${p.candidate_id}/stage`, { stage: s });
                                                                toast.success(`Moved to ${s}.`);
                                                                reload();
                                                            } catch (e) {
                                                                toast.error(e?.response?.data?.detail || "Update failed.");
                                                            }
                                                        }}
                                                        className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 transition-colors ${
                                                            isCurrent
                                                                ? "bg-ink text-white"
                                                                : gated
                                                                    ? "border border-dashed border-[#E5E5E5] text-[#A3A3A3] cursor-not-allowed opacity-60"
                                                                    : "border border-[#E5E5E5] text-[#525252] hover:border-ink"
                                                        }`}
                                                    >
                                                        {s}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Stage timeline — all past transitions with datetime + who */}
                                        {(p.stage_history || []).length > 0 && (
                                            <div data-testid={`posting-${p.candidate_id}-timeline`}>
                                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                                                    <ClockCounterClockwise size={11} weight="bold" /> Stage history
                                                </p>
                                                <ol className="border-l border-[#E5E5E5] pl-3 space-y-1.5">
                                                    {(p.stage_history || []).slice().reverse().map((h, i) => (
                                                        <li key={i} className="relative">
                                                            <span className="absolute -left-[15px] top-1.5 w-2 h-2 bg-cobalt" />
                                                            <div className="flex items-baseline gap-2 flex-wrap">
                                                                <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${stageCls(h.stage)}`}>
                                                                    {h.stage}
                                                                </span>
                                                                <span className="text-[10px] text-[#525252]">
                                                                    {fmtDateTime(h.at)}
                                                                </span>
                                                                {h.by_name && (
                                                                    <span className="text-[10px] text-[#A3A3A3]">
                                                                        · by {h.by_name}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {h.reason && (
                                                                <p className="text-[10px] text-[#525252] mt-0.5 italic">
                                                                    {h.reason}
                                                                </p>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        )}

                                        {/* Offer sub-status action */}
                                        {p.stage === "Offer" && (
                                            <div
                                                data-testid={`posting-${p.candidate_id}-offer-outcome-card`}
                                                className="border border-[#E5E5E5] bg-[#FAFAFA] p-3"
                                            >
                                                {p.offer_outcome ? (
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <div className="min-w-0">
                                                            <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">
                                                                Offer outcome
                                                            </p>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                {p.offer_outcome.status === "accepted" ? (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-[#ECFDF5] text-[#059669] px-2 py-1">
                                                                        <CheckCircle size={10} weight="fill" /> Accepted
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-[#FEF2F2] text-[#DC2626] px-2 py-1">
                                                                        <XCircle size={10} weight="fill" /> Rejected
                                                                    </span>
                                                                )}
                                                                {p.expected_joining_date && (
                                                                    <span className="text-[10px] text-[#525252]">
                                                                        Joining · {fmtDate(p.expected_joining_date)}
                                                                    </span>
                                                                )}
                                                                {p.offered_ctc_amount != null && (
                                                                    <span className="text-[10px] font-bold text-ink">
                                                                        {ctcFormat(p.offered_ctc_amount, p.offered_ctc_currency)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            data-testid={`posting-${p.candidate_id}-offer-edit-outcome`}
                                                            onClick={() => setOutcomeState({
                                                                kind: "offer", cid: p.candidate_id,
                                                                existing: p.offer_outcome,
                                                                stageHistory: p.stage_history,
                                                            })}
                                                            className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="text-[11px] text-[#525252] flex-1 min-w-0">
                                                            Record the candidate&apos;s response and lock in joining date + CTC.
                                                        </p>
                                                        <button
                                                            type="button"
                                                            data-testid={`posting-${p.candidate_id}-offer-outcome-btn`}
                                                            onClick={() => setOutcomeState({ kind: "offer", cid: p.candidate_id, existing: null, stageHistory: p.stage_history })}
                                                            className="text-[10px] font-black uppercase tracking-wider bg-ink text-white px-3 py-1.5 hover:opacity-90"
                                                        >
                                                            Record outcome
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Hired sub-status action */}
                                        {p.stage === "Hired" && (
                                            <div
                                                data-testid={`posting-${p.candidate_id}-hire-outcome-card`}
                                                className="border border-[#E5E5E5] bg-[#FAFAFA] p-3"
                                            >
                                                {p.hire_outcome ? (
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <div className="min-w-0">
                                                            <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">
                                                                Joining outcome
                                                            </p>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                {p.hire_outcome.status === "hired" ? (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-[#ECFDF5] text-[#059669] px-2 py-1">
                                                                        <CheckCircle size={10} weight="fill" /> Joined
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-[#FEF2F2] text-[#DC2626] px-2 py-1">
                                                                        <XCircle size={10} weight="fill" /> No-show
                                                                    </span>
                                                                )}
                                                                {p.actual_joining_date && (
                                                                    <span className="text-[10px] text-[#525252]">
                                                                        Joined · {fmtDate(p.actual_joining_date)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            data-testid={`posting-${p.candidate_id}-hire-edit-outcome`}
                                                            onClick={() => setOutcomeState({
                                                                kind: "hire", cid: p.candidate_id,
                                                                existing: p.hire_outcome,
                                                                stageHistory: p.stage_history,
                                                            })}
                                                            className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="text-[11px] text-[#525252] flex-1 min-w-0">
                                                            Did the candidate join? Confirm to save actual joining date.
                                                        </p>
                                                        <button
                                                            type="button"
                                                            data-testid={`posting-${p.candidate_id}-hire-outcome-btn`}
                                                            onClick={() => setOutcomeState({ kind: "hire", cid: p.candidate_id, existing: null, stageHistory: p.stage_history })}
                                                            className="text-[10px] font-black uppercase tracking-wider bg-ink text-white px-3 py-1.5 hover:opacity-90"
                                                        >
                                                            Confirm joining
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* AI match summary */}
                                        {p.match_summary && (
                                            <div className="bg-[#F9F9F9] p-3">
                                                {Array.isArray(p.match_summary.strengths) && p.match_summary.strengths.length > 0 && (
                                                    <div className="mb-2">
                                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#059669] mb-1">Strengths</p>
                                                        <ul className="text-xs text-[#525252] list-disc pl-4 space-y-0.5">
                                                            {p.match_summary.strengths.map((x, i) => <li key={i}>{x}</li>)}
                                                        </ul>
                                                    </div>
                                                )}
                                                {Array.isArray(p.match_summary.gaps) && p.match_summary.gaps.length > 0 && (
                                                    <div>
                                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#DC2626] mb-1">Gaps</p>
                                                        <ul className="text-xs text-[#525252] list-disc pl-4 space-y-0.5">
                                                            {p.match_summary.gaps.map((x, i) => <li key={i}>{x}</li>)}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Notes */}
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                                                <ChatCenteredDots size={11} weight="bold" /> Notes ({(p.notes || []).length})
                                            </p>
                                            {(p.notes || []).length === 0 && (
                                                <p className="text-xs text-[#A3A3A3]">No notes on this posting yet.</p>
                                            )}
                                            {(p.notes || []).slice(0, 5).map((n, i) => (
                                                <div key={i} className="text-xs text-[#525252] border-l-2 border-[#E5E5E5] pl-2 py-1 mb-1">
                                                    <span className="block">{n.text || ""}</span>
                                                    <span className="block text-[10px] text-[#A3A3A3] mt-0.5">
                                                        {n.author_name || n.author_id || ""} · {fmtDate(n.at)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Interviews */}
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                                                <CalendarBlank size={11} weight="bold" /> Interviews ({(p.interviews || []).length})
                                            </p>
                                            {(p.interviews || []).length === 0 && (
                                                <p className="text-xs text-[#A3A3A3]">No interviews scheduled.</p>
                                            )}
                                            {(p.interviews || []).map((iv, i) => (
                                                <div key={i} className="text-xs text-[#525252] flex items-center justify-between gap-2 py-1">
                                                    <span className="truncate">
                                                        <span className="font-bold text-ink">R{iv.round || "1"}</span> · {iv.type || "call"} · {fmtDate(iv.scheduled_at)}
                                                    </span>
                                                    {iv.outcome && (
                                                        <span className="text-[10px] font-black uppercase tracking-wider bg-[#F5F5F5] px-1.5 py-0.5">
                                                            {iv.outcome}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <button
                                            type="button"
                                            data-testid={`posting-open-full-${p.candidate_id}`}
                                            onClick={() => onOpenPosting?.(p.candidate_id)}
                                            className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:underline inline-flex items-center gap-1"
                                        >
                                            Open full posting <ArrowRight size={10} weight="bold" />
                                        </button>
                                    </div>
                                ))}
                            </TabsContent>

                            {/* Documents */}
                            <TabsContent value="docs" className="p-4 sm:p-6 space-y-3">
                                {/* V1.5.1 — Master CV on file (R2-backed).
                                    Same widget used on the candidate dashboard,
                                    so recruiter uploads land at the exact same
                                    R2 path as candidate self-uploads. */}
                                {cid && (
                                    <div className="mb-2">
                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                            Master CV on file
                                        </p>
                                        <MyResumeCard
                                            candidateId={cid}
                                            onChange={async (payload) => {
                                                // V1.5.8.13 (Feb 17, 2026) — upload
                                                // endpoint now returns the fresh
                                                // candidate. If we got one, pass it
                                                // through directly so the Kanban row
                                                // updates from the SAME response
                                                // (no stale refetch race). Also
                                                // reload the drawer for its own
                                                // header chip. Prod bug: prior fix
                                                // (V1.5.8.3) bubbled cid only —
                                                // parent refetched but sometimes
                                                // got the stale row.
                                                await reload();
                                                try {
                                                    onCandidateChange?.(cid, payload?.candidate || null);
                                                } catch { /* soft */ }
                                            }}
                                        />
                                    </div>
                                )}
                                {documents.length === 0 ? (
                                    <p className="text-sm text-[#A3A3A3]">No documents uploaded across any posting.</p>
                                ) : (
                                    documents.map((d) => (
                                        <div key={d.candidate_id} data-testid={`doc-item-${d.candidate_id}`} className="border border-[#E5E5E5] p-3 space-y-3">
                                            <div className="flex items-start gap-3">
                                                <FileText size={20} weight="bold" className="text-[#A3A3A3] mt-0.5 shrink-0" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-sm text-ink truncate">
                                                        {d.resume_filename || "Resume.pdf"}
                                                    </p>
                                                    <p className="text-[10px] text-[#A3A3A3] truncate">
                                                        From: {d.job_title || "—"} · uploaded {fmtDate(d.uploaded_at)}
                                                        {d.resume_text_length ? ` · ${d.resume_text_length} chars parsed` : ""}
                                                    </p>
                                                </div>
                                                {d.resume_url && (
                                                    <a
                                                        href={
                                                            /* V1.5.8.40 — always use the canonical
                                                               auth-gated /storage/cv endpoint so a
                                                               stale/malformed `resume_url` on the
                                                               candidate row (e.g., "resume.pdf")
                                                               can't navigate the browser to a
                                                               404-JSON page. */
                                                            `${(process.env.REACT_APP_BACKEND_URL || '')}/api/storage/cv/${d.candidate_id}`
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`doc-download-${d.candidate_id}`}
                                                        className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:underline shrink-0"
                                                    >
                                                        Open
                                                    </a>
                                                )}
                                            </div>
                                            <ResumeActions
                                                candidate={{ id: d.candidate_id, resume_text: "yes" }}
                                                onReparsed={() => {
                                                    // V1.5.8.43 — do NOT do a full
                                                    // window.location.reload(): it dumped
                                                    // the recruiter back to the Candidates
                                                    // list because the underlying route
                                                    // is /candidates. Refresh the drawer
                                                    // in place using the existing local
                                                    // reload() closure so the new
                                                    // extracted fields (phone / location
                                                    // / skills / ...) appear inline
                                                    // without navigating away.
                                                    reload();
                                                }}
                                            />
                                        </div>
                                    ))
                                )}
                            </TabsContent>

                            {/* Activity timeline */}
                            <TabsContent value="activity" className="p-4 sm:p-6">
                                {activity.length === 0 ? (
                                    <p className="text-sm text-[#A3A3A3]">No activity yet.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {activity.map((a, i) => (
                                            <div key={i} data-testid={`activity-item-${i}`} className="flex items-start gap-3">
                                                <ActivityDot kind={a.kind} />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm text-ink">{a.text}</p>
                                                    <p className="text-[10px] text-[#A3A3A3] mt-0.5">
                                                        <ActivityIcon size={9} weight="bold" className="inline mr-1" />
                                                        {a.kind === "note" && a.author ? `${a.author} · ` : ""}
                                                        {fmtDate(a.at)}
                                                        {a.job_title ? ` · ${a.job_title}` : ""}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    )}
                </div>

                {/* Reach-out compose overlay (renders inside the sheet so it doesn't stack a second modal) */}
                {showReachOut && (
                    <div data-testid="person-reach-out-modal" className="absolute inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-3">
                        <div className="bg-white w-full sm:max-w-lg shadow-2xl border border-[#E5E5E5]">
                            <div className="px-4 sm:px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
                                <p className="text-sm font-black uppercase tracking-[0.15em] text-ink">
                                    Reach out to {person.name}
                                </p>
                                <button onClick={() => setShowReachOut(false)} className="p-1 hover:bg-[#F5F5F5]">
                                    <X size={16} weight="bold" />
                                </button>
                            </div>
                            <div className="p-4 sm:p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                                {/* Channel switch */}
                                <div className="flex gap-1.5">
                                    <button
                                        type="button"
                                        data-testid="reach-channel-email"
                                        onClick={() => setReachChannel("email")}
                                        className={`flex-1 px-3 py-2 text-[10px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1 ${
                                            reachChannel === "email" ? "bg-ink text-white" : "border border-[#E5E5E5] text-[#525252]"
                                        }`}
                                    >
                                        <EnvelopeSimple size={12} weight="bold" /> Email
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="reach-channel-whatsapp"
                                        onClick={() => setReachChannel("whatsapp")}
                                        disabled={!person.phone}
                                        title={person.phone ? "" : "No phone on file"}
                                        className={`flex-1 px-3 py-2 text-[10px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1 disabled:opacity-50 ${
                                            reachChannel === "whatsapp" ? "bg-[#059669] text-white" : "border border-[#E5E5E5] text-[#525252]"
                                        }`}
                                    >
                                        <WhatsappLogo size={12} weight="bold" /> WhatsApp
                                    </button>
                                </div>

                                {/* Template picker */}
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1.5">Template</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {Object.entries(REACH_TEMPLATES).map(([k, t]) => (
                                            <button
                                                key={k}
                                                type="button"
                                                data-testid={`reach-template-${k}`}
                                                onClick={() => applyReachTemplate(k)}
                                                className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 border ${
                                                    reachTemplate === k ? "bg-ink text-white border-ink" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-ink"
                                                }`}
                                            >
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {reachChannel === "email" && (
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1.5">Subject</p>
                                        <input
                                            data-testid="reach-subject"
                                            value={reachSubject}
                                            onChange={(e) => setReachSubject(e.target.value)}
                                            className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                        />
                                    </div>
                                )}

                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1.5">
                                        Message · <span className="normal-case tracking-normal text-[#A3A3A3]">variables auto-filled from person</span>
                                    </p>
                                    <textarea
                                        data-testid="reach-body"
                                        value={reachBody}
                                        onChange={(e) => setReachBody(e.target.value)}
                                        rows={8}
                                        className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt resize-y font-mono"
                                    />
                                </div>

                                <p className="text-[10px] text-[#A3A3A3]">
                                    Opens {reachChannel === "whatsapp" ? "WhatsApp Web / app" : "your default mail client"} — nothing is sent from the server. You can edit before sending.
                                </p>
                            </div>
                            <div className="px-4 sm:px-5 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowReachOut(false)}
                                    className="px-3 py-2 text-[10px] font-black uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    data-testid="reach-send-btn"
                                    onClick={sendReachOut}
                                    disabled={reachChannel === "email" ? !person.email : !person.phone}
                                    className={`px-3 py-2 text-[10px] font-black uppercase tracking-wider text-white inline-flex items-center gap-1 disabled:opacity-50 ${
                                        reachChannel === "whatsapp" ? "bg-[#059669]" : "bg-cobalt"
                                    }`}
                                >
                                    {reachChannel === "whatsapp" ? <WhatsappLogo size={12} weight="bold" /> : <PaperPlaneTilt size={12} weight="bold" />}
                                    Open draft
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </SheetContent>
            {outcomeState && (
                <OfferHireOutcomeModal
                    kind={outcomeState.kind}
                    candidateId={outcomeState.cid}
                    existing={outcomeState.existing}
                    stageHistory={outcomeState.stageHistory}
                    onClose={() => setOutcomeState(null)}
                    onSaved={() => { setOutcomeState(null); reload(); }}
                />
            )}
            {showReassign && person && (
                <ReassignCandidateModal
                    candidateId={cid}
                    candidateName={person.name}
                    currentOwnerId={person.owner_id || data?.owner_id}
                    currentOwnerName={person.added_by_name || data?.added_by_name}
                    isTeamPool={!!(person.is_team_pool || data?.is_team_pool)}
                    onClose={() => setShowReassign(false)}
                    onSaved={() => { setShowReassign(false); reload(); }}
                />
            )}
        </Sheet>
    );
}
